import { useCallback, useEffect, useMemo, useState, useRef } from "react";

import "./stylesEventsList.css";
import CustomDatePicker from "../../CommonComponents/CustomDatePicker/CustomDatePicker";

import dayjs from "dayjs";
import { ChevronDown, ChevronUp, Filter } from "react-feather";
import { message, Pagination, Select, Table } from "antd";
import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../api/ApiRoutes";
import RebetLoader from "../../CommonComponents/RebetLoader/RebetLoader";
import { useNavigate } from "react-router-dom";
import CustomAutoCompleteControlled from "../../CommonComponents/CustomAutoComplete/CustomAutoCompleteControlled";
import { PushpinOutlined } from "@ant-design/icons";

import unpinIcon from "../../../assets/icons/ic_unpin.svg";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { useSelector } from "react-redux";
import { getAdminNow } from "../../../utils/dateService";

function EventsList({ children = undefined }) {
  const { appConfig } = useSelector((state) => state.config);
  const token = JSON.parse(localStorage.getItem("token"));
  const todayRange = useMemo(() => [getAdminNow(), getAdminNow()], []);
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  const [isApiRunning, setIsApiRunning] = useState(false);
  const [showExtraFilters, setShowExtraFilters] = useState(false);
  const [sportsBookDates, setSportsBookDates] = useState(todayRange);

  const [pinnedEvents, setPinnedEvents] = useState(() => {
    const stored = localStorage.getItem("pinnedEvents");
    return stored ? JSON.parse(stored) : [];
  });

  const [selectedSport, setSelectedSport] = useState(null);
  const [selectedSportId, setSelectedSportId] = useState(null);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [selectedTournamentId, setSelectedTournamentId] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedBetId, setSelectedBetId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedEventType, setSelectedEventType] = useState("match");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [eventSearchTerm, setEventSearchTerm] = useState("");

  const [sports, setSports] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [providers, setProviders] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const [betsToShowInTable, setBetsToShowInTable] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 20,
    total_results: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });
  const [, setBetsCache] = useState({});
  const betsCacheRef = useRef({});
  const eventsRef = useRef([]);

  const [filterType, setFilterType] = useState(appConfig?.app_currency_config?.primary_currency?.shortcode);
  const filtersRestoredRef = useRef(false);
  const shouldFetchAfterRestoreRef = useRef(false);
  const [restoreFetchTrigger, setRestoreFetchTrigger] = useState(0);


  // Memoize PrivateAdminPanelInstance to recreate when token changes
  const PrivateAdminPanelInstanceMemo = useMemo(() => {
    return createPrivateAdminPanelInstance(token?.AccessToken, false);
  }, [token?.AccessToken]);

  // Fetch dropdowns from API
  const fetchProviders = useCallback(async () => {
    try {
      const response = await PrivateAdminPanelInstanceMemo?.get(
        API_ROUTES?.GET_SPORTSBOOK_EVENTS_LIST_PROVIDERS_DROPDOWN
      );
      const data = response?.data?.data || [];
      setProviders(
        data.map((item) => ({
          value: item.provider || item.value,
          label: item.title || item.label,
        }))
      );
    } catch (error) {
      console.error("Error fetching providers:", error);
      setProviders([]);
    }
  }, [PrivateAdminPanelInstanceMemo]);

  const fetchEventTypes = useCallback(async () => {
    try {
      const response = await PrivateAdminPanelInstanceMemo?.get(
        API_ROUTES?.GET_SPORTSBOOK_EVENTS_LIST_EVENT_TYPE_DROPDOWN
      );
      const data = response?.data?.data || [];
      setEventTypes(
        data.map((item) => ({
          value: item.event_type || item.value,
          label: item.title || item.label,
        }))
      );
    } catch (error) {
      console.error("Error fetching event types:", error);
      setEventTypes([
        { value: "match", label: "Match" },
        { value: "outright", label: "Outright" },
      ]);
    }
  }, [PrivateAdminPanelInstanceMemo]);

  const fetchStatuses = useCallback(async (eventType) => {
    try {
      const params = new URLSearchParams();
      if (eventType) {
        params.append("event_type", eventType);
      }
      const response = await PrivateAdminPanelInstanceMemo?.get(
        `${API_ROUTES?.GET_SPORTSBOOK_EVENTS_LIST_STATUS_DROPDOWN}?${params.toString()}`
      );
      const data = response?.data?.data || [];
      const statusOptions = data.map((item) => ({
        value: item.status || item.value,
        label: item.title || item.label,
      }));
      // Add "All" option
      setStatuses([{ value: null, label: "All" }, ...statusOptions]);
    } catch (error) {
      console.error("Error fetching statuses:", error);
      setStatuses([
        { value: null, label: "All" },
        { value: "future", label: "Future" },
        { value: "live", label: "Live" },
        { value: "canceled", label: "Canceled" },
        { value: "postponed", label: "Postponed" },
        { value: "completed", label: "Completed" },
      ]);
    }
  }, [PrivateAdminPanelInstanceMemo]);

  const fetchSports = useCallback(async (searchTerm = null, searchLimit = null) => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append("search_term", searchTerm);
      if (searchLimit) params.append("search_limit", searchLimit);
      const response = await PrivateAdminPanelInstanceMemo?.get(
        `${API_ROUTES?.GET_SPORTSBOOK_EVENTS_LIST_SPORTS_DROPDOWN}?${params.toString()}`
      );
      const data = response?.data?.data || [];
      setSports(
        data.map((item) => ({
          value: item.title,
          label: item.title || item.label,
          sport_id: item.sport_id || null,
        }))
      );
    } catch (error) {
      console.error("Error fetching sports:", error);
      setSports([]);
    }
  }, [PrivateAdminPanelInstanceMemo]);

  const fetchLeagues = useCallback(async (sportId = null, searchTerm = null, searchLimit = null) => {
    try {
      const params = new URLSearchParams();
      if (sportId) params.append("sport_id", sportId);
      if (searchTerm) params.append("search_term", searchTerm);
      if (searchLimit) params.append("search_limit", searchLimit);
      const response = await PrivateAdminPanelInstanceMemo?.get(
        `${API_ROUTES?.GET_SPORTSBOOK_EVENTS_LIST_LEAGUES_DROPDOWN}?${params.toString()}`
      );
      const data = response?.data?.data || [];
      setLeagues(
        data.map((item) => ({
          value: item.title,
          label: item.title || item.label,
          tournament_id: item.tournament_id || null,
        }))
      );
    } catch (error) {
      console.error("Error fetching leagues:", error);
      setLeagues([]);
    }
  }, [PrivateAdminPanelInstanceMemo]);

  const fetchEvents = useCallback(async (params, searchTerm = "") => {
    try {
      const queryParams = new URLSearchParams();
      
      // Required: event_type and search_term
      if (params.event_type) {
        queryParams.append("event_type", params.event_type);
      } else if (selectedEventType) {
        queryParams.append("event_type", selectedEventType);
      }
      
      // search_term is required - use provided searchTerm or empty string
      queryParams.append("search_term", searchTerm || "");
      
      // Add other params
      Object.keys(params).forEach((key) => {
        if (key !== "event_type" && params[key] !== null && params[key] !== undefined && params[key] !== "") {
          queryParams.append(key, params[key]);
        }
      });
      
      // I need to check if there is sport_id or tournament_id
      if (!params.sport_id && !params.tournament_id) return;

      const response = await PrivateAdminPanelInstanceMemo?.get(
        `${API_ROUTES?.GET_SPORTSBOOK_EVENTS_LIST_EVENTS_DROPDOWN}?${queryParams.toString()}`
      );
      const data = response?.data?.data || [];
      const nextEvents = data.map((event) => ({
          value: event.title,
          label: event.title || event.label || event.event_name || event.name || event.event_id || event.id,
          event_id: event.event_id || event.id,
        }));
      eventsRef.current = nextEvents;
      setEvents(nextEvents);
    } catch (error) {
      /* istanbul ignore start - dropdown error path */
      console.error("Error fetching events:", error);
      eventsRef.current = [];
      setEvents([]);
      /* istanbul ignore end */
    }
  }, [PrivateAdminPanelInstanceMemo, selectedEventType]);

  const togglePin = (event) => {
    const isPinned = pinnedEvents.some(
      (pinnedEvent) => pinnedEvent.id === event.id
    );

    const newPinnedEvents = isPinned
      ? pinnedEvents.filter((pinnedEvent) => pinnedEvent.id !== event.id)
      : [...pinnedEvents, event];

    setPinnedEvents(newPinnedEvents);
    localStorage.setItem("pinnedEvents", JSON.stringify(newPinnedEvents));
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 100,
      render: (text, record) => {
        const isPinned =
          pinnedEvents.filter((pinnedEvent) => pinnedEvent.id === record.id)
            .length > 0;
        return (
          <div className="events-list-id-container">
            {isPinned ? (
              <img
                src={unpinIcon}
                alt="Unpin"
                className="events-list-id-icon pinned"
                onClick={() => togglePin(record)}
                data-testid="event-pin-toggle"
              />
            ) : (
              <PushpinOutlined
                className="events-list-id-icon unpinned"
                onClick={() => togglePin(record)}
                data-testid="event-pin-toggle"
              />
            )}
            <p className="events-list-id-text">{record.id}</p>
          </div>
        );
      },
    },
    {
      title: "Event Name",
      dataIndex: "name",
      key: "name",
      width: 150,
      render: (text, record) => (
        <div className="events-list-event-name-container">
          {record.away_logo && record.home_logo && (
            <div className="events-list-event-name-img-container">
              <img
                src={record.away_logo}
                alt="Event Logo"
                className="events-list-away-logo"
              />
              <img
                src={record.home_logo}
                alt="Event Logo"
                className="events-list-home-logo"
              />
            </div>
          )}
          <p
            className="events-list-event-name-text"
            onClick={() => handleEventClick(record.id)}
          >
            {text}
          </p>
        </div>
      ),
    },
    {
      title: "Date & Time",
      dataIndex: "scheduled",
      key: "scheduled",
      width: 80,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 50,
      render: (text, record) => (
        <p className={`events-list-status ${record.status?.toLowerCase()}`}>
          {record.status}
        </p>
      ),
    },
    {
      title: "Sport",
      dataIndex: "sport",
      key: "sport",
      width: 50,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 80,
    },
    {
      title: "League",
      dataIndex: "league",
      key: "league",
      width: 100,
      render: (text, record) => (
        <div className="events-list-league-container">
          {record.league_logo && (
            <img
              src={record.league_logo}
              alt="League Logo"
              className="events-list-league-logo"
            />
          )}
          <p className="events-list-league-text">{text}</p>
        </div>
      ),
    },
    {
      title: "Scores",
      dataIndex: "scores",
      key: "scores",
      width: 50,
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 80,
    },
    {
      title: "Provider",
      dataIndex: "providers",
      key: "providers",
      width: 50,
      render: (text, record) =>
        record.providers.length > 1 ? (
          <div className="events-list-providers">
            <Select
              value={null}
              onChange={() => {}}
              onSelect={() => {}}
              placeholder={record.providers.length + " Providers"}
              options={record.providers.map((provider) => ({
                value: provider,
                label: provider,
              }))}
            />
          </div>
        ) : (
          <p style={{ margin: 0 }}>{record.providers[0]}</p>
        ),
    },
    {
      title: () => (
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span>Bet Amount</span>
          {/* <div className="bet-amount-buttons">
            <Button
              className={filterType === appConfig?.app_currency_config?.primary_currency?.shortcode ? "active" : ""}
              onClick={() => handleFilterTypeChange(appConfig?.app_currency_config?.primary_currency?.shortcode)}
            >
              <img src={cashIcon} alt="Cash" />
            </Button>
            <Button
              className={filterType === "rebet_free_cash" ? "active" : ""}
              onClick={() => handleFilterTypeChange("rebet_free_cash")}
            >
              <img src={freeCashIcon} alt="Free Cash" />
            </Button>
          </div> */}
        </div>
      ),
      dataIndex: "bet_amount",
      key: "bet_amount",
      width: 100,
      render: (text, record) => (
        <div className="bets-report-bet-amount-container">
          {/* {filterType === appConfig?.app_currency_config?.primary_currency?.shortcode ? (
            <img src={cashIcon} alt="Cash" />
          ) : filterType === "rebet_free_cash" ? (
            <img src={freeCashIcon} alt="Free Cash" />
          ) : (
            ""
          )} */}
          <span className="bets-report-bet-amount-text">
            {record.bet_amount === "-"
              ? "-"
              : Intl.NumberFormat("en-US", {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                }).format(
                  record.bet_amount
                )}
          </span>
        </div>
      ),
    },
    {
      title: "Number of Bets",
      dataIndex: "bet_count",
      key: "bet_count",
      width: 80,
      render: (text, record) => (
        <span className="bets-report-bet-amount-text">
          {Intl.NumberFormat("en-US", {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          }).format(record.bet_count)}
        </span>
      ),
    },
  ];

  const reorderedData = useMemo(() => {
    const pinnedIds = pinnedEvents.map((e) => e.id);

    const pinned = pinnedEvents;
    const unpinned = betsToShowInTable.filter(
      (item) => !pinnedIds.includes(item.id)
    );

    return [...pinned, ...unpinned];
  }, [betsToShowInTable, pinnedEvents]);

  const getEventsList = useCallback(async (
    page = 1,
    refresh = false,
    queryString = todayRange,
    isQueryString = true,
    _updateFilters = true,
    lastEvaluatedKey = null
  ) => {
    if (!token?.AccessToken) {
      messageApi.error(AppStrings.PAM_AUTH_TOKEN_NOT_FOUND);
      return;
    }

    let startDate = "";
    let endDate = "";

    if (isQueryString) {
      const formattedQueryString = queryString.split("&");
      startDate = dayjs(formattedQueryString[0]).format("YYYY-MM-DD");
      endDate = dayjs(formattedQueryString[1]).format("YYYY-MM-DD");
    } else {
      startDate = queryString[0]?.format("YYYY-MM-DD");
      endDate = queryString[1]?.format("YYYY-MM-DD");
    }

    // Build query parameters based on event_type
    const params = new URLSearchParams();
    
    // If event_id is provided, only send event_id
    const selectedEventId = eventsRef.current.find((event) => event.label === selectedEvent)?.event_id || null;
    if (selectedEventId) {
      params.append("event_id", selectedEventId);
    } else {
      // Add event_type to params
      if (selectedEventType) {
        params.append("event_type", selectedEventType);
      }
      
      // Build params based on event_type
      if (selectedEventType === "outright") {
        /* istanbul ignore next - validation when outright selected without sport */
        if (!selectedSportId) {
          messageApi.error(AppStrings.SPORTSBOOK_OUTRIGHT_EVENTS_SPORT_REQUIRED);
          return;
        }
        params.append("sport_id", selectedSportId);
        
        // Optional: tournament_id, league_id, status, page, page_size, last_evaluated_key
        if (selectedTournamentId) {
          params.append("tournament_id", selectedTournamentId);
        } else if (selectedLeague) {
          params.append("league_id", selectedLeague);
        }
        // Status for outright: only "future" or "completed"
        if (selectedStatus && selectedStatus !== "All" && selectedStatus !== null) {
          if (selectedStatus === "future" || selectedStatus === "completed") {
            params.append("status", selectedStatus);
          }
        }
      } else if (selectedEventType === "match") {
        // Required: start_date, end_date - don't proceed if missing
        if (!startDate || !endDate) {
          messageApi.error(AppStrings.SPORTSBOOK_MATCH_EVENTS_DATE_RANGE_REQUIRED);
          return;
        }
        params.append("start_date", startDate);
        params.append("end_date", endDate);
        
        // Optional: sport_id and tournament_id (can be passed together)
        if (selectedSportId) {
          params.append("sport_id", selectedSportId);
        }
        if (selectedTournamentId) {
          params.append("tournament_id", selectedTournamentId);
        }
        // Optional: league_id, status, provider
        if (selectedLeague && !selectedTournamentId) {
          params.append("league_id", selectedLeague);
        }
        // Status for match: "future", "live", "canceled", "postponed", or "completed"
        // Validate status based on date before adding
        if (selectedStatus && selectedStatus !== "All" && selectedStatus !== null) {
          const endDateObj = queryString && queryString.length === 2 ? queryString[1] : null;
          if (endDateObj) {
            const isPast = dayjs(endDateObj).isBefore(getAdminNow(), "day");
            const isFuture = dayjs(endDateObj).isAfter(getAdminNow(), "day");
            
            // Don't send "live" status for past dates - use "completed" instead
            if (isPast && (selectedStatus === "live" || selectedStatus === "Live")) {
              params.append("status", "completed");
            }
            // Don't send "live" status for future dates - skip status param completely
            // This prevents 422 errors from the API
            else if (isFuture && (selectedStatus === "live" || selectedStatus === "Live")) {
              // Explicitly don't add status param - this is critical to prevent 422 errors
            } else {
              // Valid combination - add status
              params.append("status", selectedStatus);
            }
          } else {
            params.append("status", selectedStatus);
          }
        }
        if (selectedProvider) {
          params.append("provider", selectedProvider);
        }
      }

      // Common optional params
      params.append("page", page);
      params.append("page_size", 20);
      if (lastEvaluatedKey) {
        params.append("last_evaluated_key", lastEvaluatedKey);
      }
    }

    const cacheKey = page;

    if (betsCacheRef.current[cacheKey] && !refresh) {
      const cached = betsCacheRef.current[cacheKey];
      setBetsToShowInTable(cached.bets);
      setPagination(cached.pagination);
      return;
    }

    try {
      setIsApiRunning(true);

      const response = await PrivateAdminPanelInstanceMemo?.get(
        `${API_ROUTES?.GET_SPORTSBOOK_EVENTS_LIST}?${params.toString()}`
      );
      const data = response.data.data;
      const bets = data.events || [];

      // Transform event data if needed
      const transformedBets = bets.map((bet) => {
        // Handle event_name if it's an object
        let eventName = bet.name || bet.event_name;
        if (typeof eventName === "object") {
          eventName = eventName.title || eventName.name || JSON.stringify(eventName);
        }

        // Handle scores if it's an object
        let scores = bet.scores || "-";
        if (typeof scores === "object") {
          scores = `${scores.home || ""} : ${scores.away || ""}`;
        }

        return {
          ...bet,
          name: eventName,
          scores: scores,
          bet_amount: bet.bet_amount   ??bet.bet_volume.rebet_cash.log_odds_weighted
          // bet_amount: bet.bet_count > 1 ? bet.bet_volume.rebet_cash.log_odds_weighted : bet.bet_amount
        };
      });

      const paginationData = {
        current_page: data?.pagination?.current_page || page,
        page_size: 20,
        total_results: data?.pagination?.has_next
          ? data?.pagination?.current_page * 20 + 1
          : data?.pagination?.current_page * 20,
        has_next: data?.pagination?.has_next || false,
        has_prev: data?.pagination?.has_prev || false,
      };

      setBetsToShowInTable(transformedBets);
      setPagination(paginationData);
      setLastEvaluatedKey(data?.last_evaluated_key || null);

      setBetsCache((prev) => {
        const nextCache = {
          ...prev,
          [cacheKey]: {
            bets: transformedBets,
            pagination: paginationData,
          },
        };
        betsCacheRef.current = nextCache;
        return nextCache;
      });
    } catch (error) {
      messageApi.error(error?.response?.data?.message || "Something went wrong.");
      console.error("Error fetching events list:", error);
    } finally {
      setIsApiRunning(false);
    }
  }, [
    PrivateAdminPanelInstanceMemo,
    messageApi,
    selectedEvent,
    selectedEventType,
    selectedLeague,
    selectedProvider,
    selectedSportId,
    selectedStatus,
    selectedTournamentId,
    todayRange,
    token?.AccessToken,
  ]);

  const handlePaginationChange = (page) => {
    const queryStringParams = sportsBookDates ? sportsBookDates : todayRange;

    getEventsList(
      page,
      false,
      queryStringParams,
      false,
      false,
      lastEvaluatedKey
    );
  };

  const handleLeagueChange = (value) => {
    // value is the tournament_id, find the label to display
    const leagueOption = leagues.find((league) => league.value === value);
    setSelectedLeague(leagueOption ? leagueOption.label : value);

    if (leagueOption && leagueOption.tournament_id) {
    setSelectedTournamentId(leagueOption ? leagueOption.tournament_id : null);
    }
  };

  const handleProviderChange = (value) => {
    setSelectedProvider(value);
  };

  const handleSportChange = (value) => {
    // value is the sport_id, find the label to display
    const sportOption = sports.find((sport) => sport.value === value);
    setSelectedSport(sportOption ? sportOption.label : value);
    
    // Fetch leagues for this sport
    if (sportOption && sportOption.sport_id) {
      fetchLeagues(sportOption.sport_id);
      setSelectedSportId(sportOption.sport_id);
    }
    setSelectedLeague(null);
    setSelectedTournamentId(null);
  };

  const handleStatusChange = (value) => {
    setSelectedStatus(value);
  };

  const handleEventSearch = (searchText) => {
    setEventSearchTerm(searchText || "");
    // Trigger API call with new search term
    if (selectedEventType) {
      const fetchEventsWithSearch = async () => {
        let startDate = "";
        let endDate = "";

        if (sportsBookDates && sportsBookDates.length === 2) {
          startDate = sportsBookDates[0]?.format("YYYY-MM-DD") || "";
          endDate = sportsBookDates[1]?.format("YYYY-MM-DD") || "";
        }

        const params = {
          event_type: selectedEventType,
        };

        if (selectedEventType === "outright" && selectedSportId) {
          params.sport_id = selectedSportId;
          if (selectedTournamentId) {
            params.tournament_id = selectedTournamentId;
          } else if (selectedLeague) {
            params.league_id = selectedLeague;
          }
          if (selectedStatus && selectedStatus !== "All" && selectedStatus !== null) {
            params.status = selectedStatus;
          }
          fetchEvents(params, searchText || "");
        } else if (selectedEventType === "match" && startDate && endDate) {
          params.start_date = startDate;
          params.end_date = endDate;
          // Optional: sport_id and tournament_id (can be passed together)
          if (selectedSportId) {
            params.sport_id = selectedSportId;
          }
          if (selectedTournamentId) {
            params.tournament_id = selectedTournamentId;
          }
          if (selectedLeague && !selectedTournamentId) {
            params.league_id = selectedLeague;
          }
          if (selectedStatus && selectedStatus !== "All" && selectedStatus !== null) {
            params.status = selectedStatus;
          }
          if (selectedProvider) {
            params.provider = selectedProvider;
          }
          fetchEvents(params, searchText || "");
        }
      };
      fetchEventsWithSearch();
    }
  };

  const handleEventTypeChange = (value) => {
    setSelectedEventType(value);
    // Reset status and fetch new statuses
    setSelectedStatus(null);
    fetchStatuses(value);
    // Reset sport-related filters for outright
    if (value === "outright") {
      setSelectedSport(null);
      setSelectedSportId(null);
      setSelectedTournamentId(null);
    }
  };

  const saveFiltersToLocalStorage = () => {
    const filters = {
      sportsBookDates: sportsBookDates ? [
        sportsBookDates[0]?.format("YYYY-MM-DD"),
        sportsBookDates[1]?.format("YYYY-MM-DD")
      ] : null,
      selectedSport,
      selectedSportId,
      selectedLeague,
      selectedTournamentId,
      selectedProvider,
      selectedBetId,
      selectedEventType,
      selectedStatus,
      eventSearchTerm,
      showExtraFilters,
      filterType,
      pagination: {
        current_page: pagination.current_page,
        page_size: pagination.page_size,
      },
    };
    localStorage.setItem("eventsListFilters", JSON.stringify(filters));
  };

  const restoreFiltersFromLocalStorage = useCallback(() => {
    const savedFilters = localStorage.getItem("eventsListFilters");
    if (savedFilters) {
      try {
        const filters = JSON.parse(savedFilters);
        
        // Restore dates first
        let restoredDates = null;
        if (filters.sportsBookDates) {
          restoredDates = [
            dayjs(filters.sportsBookDates[0]),
            dayjs(filters.sportsBookDates[1])
          ];
          setSportsBookDates(restoredDates);
        }
        
        // Restore event type first (needed for status validation)
        if (filters.selectedEventType !== undefined) {
          setSelectedEventType(filters.selectedEventType);
        }
        
        // Validate and adjust status based on date
        let statusToRestore = filters.selectedStatus;
        /* istanbul ignore start - date-based status adjustment for restored filters */
        if (restoredDates && restoredDates.length === 2 && filters.selectedEventType === "match") {
          const endDate = restoredDates[1];
          const isPast = endDate && dayjs(endDate).isBefore(getAdminNow(), "day");
          const isFuture = endDate && dayjs(endDate).isAfter(getAdminNow(), "day");
          if (isPast && (statusToRestore === "live" || statusToRestore === "Live")) {
            statusToRestore = "completed";
          }
          else if (isFuture && (statusToRestore === "live" || statusToRestore === "Live")) {
            statusToRestore = null;
          }
        }
        /* istanbul ignore end */
        
        // Restore sport - convert ID to label if needed
        if (filters.selectedSportId !== undefined) {
          setSelectedSportId(filters.selectedSportId);
          // Will be updated to label in useEffect after sports load
        }
        if (filters.selectedSport !== undefined) {
          // If it looks like an ID (contains "sr:sport:"), we'll convert it in useEffect
          // Otherwise, use it as is (might already be a label)
          setSelectedSport(filters.selectedSport);
        }
        // Restore league - convert ID to label if needed
        if (filters.selectedTournamentId !== undefined) {
          setSelectedTournamentId(filters.selectedTournamentId);
          // Will be updated to label in useEffect after leagues load
        }
        if (filters.selectedLeague !== undefined) {
          // If it looks like an ID, we'll convert it in useEffect
          setSelectedLeague(filters.selectedLeague);
        }
        if (filters.selectedSport !== undefined) setSelectedSport(filters.selectedSport);
        if (filters.selectedSportId !== undefined) setSelectedSportId(filters.selectedSportId);
        if (filters.selectedLeague !== undefined) setSelectedLeague(filters.selectedLeague);
        if (filters.selectedTournamentId !== undefined) setSelectedTournamentId(filters.selectedTournamentId);
        if (filters.selectedProvider !== undefined) setSelectedProvider(filters.selectedProvider);
        if (filters.selectedBetId !== undefined) setSelectedBetId(filters.selectedBetId);
        if (statusToRestore !== undefined) setSelectedStatus(statusToRestore);
        if (filters.eventSearchTerm !== undefined) setEventSearchTerm(filters.eventSearchTerm);
        if (filters.showExtraFilters !== undefined) setShowExtraFilters(filters.showExtraFilters);
        if (filters.filterType !== undefined) setFilterType(filters.filterType);
        if (filters.pagination) {
          setPagination(prev => ({
            ...prev,
            current_page: filters.pagination.current_page,
            page_size: filters.pagination.page_size,
          }));
        }
        
        return true; // Indicates filters were restored
      } catch (error) {
        console.error("Error restoring filters from localStorage:", error);
        localStorage.removeItem("eventsListFilters");
        return false;
      }
    }
    return false;
  }, []);

  const handleEventClick = (id) => {
    saveFiltersToLocalStorage();
    navigate(`/event-details?event-id=${id}`);
  };

  const handleClearFilters = () => {
    setSelectedBetId(null);
    setSelectedSport(null);
    setSelectedSportId(null);
    setSelectedLeague(null);
    setSelectedTournamentId(null);
    setSelectedProvider(null);
    setSelectedStatus("live");
    setSelectedEvent(null);
    setSelectedEventType("match");
    // Clear saved filters from localStorage
    localStorage.removeItem("eventsListFilters");
  };

  const handleApplyFilters = useCallback(() => {
    getEventsList(1, true, sportsBookDates, false, false, null);
  }, [getEventsList, sportsBookDates]);

  const getEventsListRef = useRef(getEventsList);
  const sportsBookDatesRef = useRef(sportsBookDates);
  const todayRangeRef = useRef(todayRange);
  const restoreFiltersFromLocalStorageRef = useRef(restoreFiltersFromLocalStorage);

  useEffect(() => {
    getEventsListRef.current = getEventsList;
    sportsBookDatesRef.current = sportsBookDates;
    todayRangeRef.current = todayRange;
    restoreFiltersFromLocalStorageRef.current = restoreFiltersFromLocalStorage;
  }, [getEventsList, restoreFiltersFromLocalStorage, sportsBookDates, todayRange]);

  const handleDateChange = (queryString) => {
    getEventsList(1, true, queryString, true, false, null);
  };

  // Initialize dropdowns on mount
  useEffect(() => {
    fetchProviders();
    fetchEventTypes();
    fetchStatuses(selectedEventType);
    fetchSports();
  }, [fetchEventTypes, fetchProviders, fetchSports, fetchStatuses, selectedEventType]);

  // Update display values (labels) when sports/leagues/events data loads and we have IDs stored
  useEffect(() => {
    if (selectedSportId && sports.length > 0) {
      const sportOption = sports.find((sport) => sport.value === selectedSportId);
      if (sportOption && selectedSport !== sportOption.label) {
        setSelectedSport(sportOption.label);
      } else if (!sportOption && selectedSport && selectedSport.includes("sr:sport:")) {
        // If we have an ID stored but no matching option, keep the ID for now
        // This might happen if the sport was removed or data hasn't loaded yet
      }
    }
  }, [selectedSport, selectedSportId, sports]);

  useEffect(() => {
    if (selectedTournamentId && leagues.length > 0) {
      const leagueOption = leagues.find((league) => league.value === selectedTournamentId);
      if (leagueOption && selectedLeague !== leagueOption.label) {
        setSelectedLeague(leagueOption.label);
      }
    }
  }, [leagues, selectedLeague, selectedTournamentId]);

  // Fetch statuses when event type changes
  useEffect(() => {
    if (selectedEventType) {
      fetchStatuses(selectedEventType);
    }
  }, [fetchStatuses, selectedEventType]);

  // Update status filter when date range is in the past
  useEffect(() => {
    if (sportsBookDates && sportsBookDates.length === 2 && selectedEventType === "match") {
      const endDate = sportsBookDates[1];
      const isPast = endDate && dayjs(endDate).isBefore(getAdminNow(), "day");
      if (isPast && (selectedStatus === "live" || selectedStatus === "Live")) {
        setSelectedStatus("completed");
      }
    }
  }, [sportsBookDates, selectedStatus, selectedEventType]);

  // Update status when a specific event is selected
  useEffect(() => {
    if (selectedEvent) {
      const event = events.find((e) => e.label === selectedEvent);
      if (event?.status) {
        setSelectedStatus(event.status);
      }
    }
  }, [selectedEvent, events]);

  // Fetch events dropdown when filters change
  useEffect(() => {
    // Skip fetching if filters are being restored (to avoid invalid API calls)
    if (filtersRestoredRef.current) {
      return;
    }

    const fetchEventsDropdown = async () => {
      if (!selectedEventType) return;

      let startDate = "";
      let endDate = "";

      if (sportsBookDates && sportsBookDates.length === 2) {
        startDate = sportsBookDates[0]?.format("YYYY-MM-DD") || "";
        endDate = sportsBookDates[1]?.format("YYYY-MM-DD") || "";
      }

      const params = {
        event_type: selectedEventType, // Always include event_type
      };

      if (selectedEventType === "outright") {
        // Required: sport_id
        if (selectedSportId) {
          params.sport_id = selectedSportId;
        }
        // Optional: tournament_id, league_id, status
        if (selectedTournamentId) {
          params.tournament_id = selectedTournamentId;
        } else if (selectedLeague) {
          params.league_id = selectedLeague;
        }
        if (selectedStatus && selectedStatus !== "All" && selectedStatus !== null) {
          params.status = selectedStatus;
        }
      } else if (selectedEventType === "match") {
        // Required: start_date, end_date
        if (startDate && endDate) {
          params.start_date = startDate;
          params.end_date = endDate;
        }
        // Optional: sport_id and tournament_id (can be passed together)
        if (selectedSportId) {
          params.sport_id = selectedSportId;
        }
        if (selectedTournamentId) {
          params.tournament_id = selectedTournamentId;
        }
        // Optional: league_id, status, provider
        if (selectedLeague && !selectedTournamentId) {
          params.league_id = selectedLeague;
        }
        // Validate status based on date before adding to params
        if (selectedStatus && selectedStatus !== "All" && selectedStatus !== null) {
          const endDateObj = sportsBookDates && sportsBookDates.length === 2 ? sportsBookDates[1] : null;
          if (endDateObj) {
            const isPast = dayjs(endDateObj).isBefore(getAdminNow(), "day");
            const isFuture = dayjs(endDateObj).isAfter(getAdminNow(), "day");
            
            // Don't send "live" status for past dates - use "completed" instead
            if (isPast && (selectedStatus === "live" || selectedStatus === "Live")) {
              params.status = "completed";
            }
            // Don't send "live" status for future dates - don't include status param
            else if (isFuture && (selectedStatus === "live" || selectedStatus === "Live")) {
              // Explicitly don't add status param - this prevents 422 errors
            } else {
              // Valid combination - add status
              params.status = selectedStatus;
            }
          } else {
            params.status = selectedStatus;
          }
        }
        if (selectedProvider) {
          params.provider = selectedProvider;
        }
      }

      // Only fetch if we have required params
      if (selectedEventType === "outright" && selectedSportId) {
        fetchEvents(params, eventSearchTerm);
      } else if (selectedEventType === "match" && startDate && endDate) {
        fetchEvents(params, eventSearchTerm);
      } else {
        /* istanbul ignore next - clear when required params missing */
        eventsRef.current = [];
        setEvents([]);
      }
    };

    fetchEventsDropdown();
  }, [
    selectedEventType,
    selectedSportId,
    selectedTournamentId,
    selectedLeague,
    selectedStatus,
    selectedProvider,
    sportsBookDates,
    eventSearchTerm,
    fetchEvents,
  ]);

  // Fetch events when filters are restored and state is ready (e.g. when returning from Event Details)
  useEffect(() => {
    if (shouldFetchAfterRestoreRef.current && restoreFetchTrigger > 0) {
      // State is ready (all filter values have been updated by restoreFiltersFromLocalStorage)
      handleApplyFilters();
      shouldFetchAfterRestoreRef.current = false;
      filtersRestoredRef.current = false;
      setRestoreFetchTrigger(0);
    }
  }, [handleApplyFilters, restoreFetchTrigger, selectedSportId, selectedProvider, selectedTournamentId, selectedLeague, selectedStatus, selectedEventType, sportsBookDates]);

  // Restore filters on mount
  useEffect(() => {
    const savedFilters = localStorage.getItem("eventsListFilters");
    if (savedFilters) {
      try {
        const filters = JSON.parse(savedFilters);
        
        // Set flag BEFORE restoring to prevent fetchEventsDropdown from running
        filtersRestoredRef.current = true;
        
        // Restore filters (this will trigger state updates)
        restoreFiltersFromLocalStorageRef.current();
        
        // Prepare restored dates
        const restoredDates = filters.sportsBookDates ? [
          dayjs(filters.sportsBookDates[0]),
          dayjs(filters.sportsBookDates[1])
        ] : todayRangeRef.current;
        
        // Wait for all state updates to complete, then fetch events
        setTimeout(() => {
          /* istanbul ignore start - restore delay and date sync branches */
          if (restoredDates && restoredDates.length === 2) {
            const currentDates = sportsBookDatesRef.current;
            const datesMatch = currentDates && currentDates.length === 2 &&
              currentDates[0].format("YYYY-MM-DD") === restoredDates[0].format("YYYY-MM-DD") &&
              currentDates[1].format("YYYY-MM-DD") === restoredDates[1].format("YYYY-MM-DD");
            if (!datesMatch) {
              setSportsBookDates(restoredDates);
            }
          }
          shouldFetchAfterRestoreRef.current = true;
          filtersRestoredRef.current = false;
          setRestoreFetchTrigger((t) => t + 1);
          setTimeout(() => {
            localStorage.removeItem("eventsListFilters");
          }, 3000);
          /* istanbul ignore end */
        }, 1000);
      } catch (error) {
        /* istanbul ignore start - error path when saved filters are invalid */
        console.error("Error restoring filters:", error);
        localStorage.removeItem("eventsListFilters");
        filtersRestoredRef.current = false;
        getEventsListRef.current(1, true, sportsBookDatesRef.current, false, true, null);
        /* istanbul ignore end */
      }
    } else {
      /* istanbul ignore next - no saved filters, fetch with defaults */
      getEventsListRef.current(1, true, sportsBookDatesRef.current, false, true, null);
    }
  }, []);

  return (
    <div className="events-list-container">
      {contextHolder}
      <div className="events-list">
        <>
          <div className="head-controls">
            <div className="events-list-date-picker-container">
              <CustomDatePicker
                getData={handleDateChange}
                dates={sportsBookDates}
                setDates={setSportsBookDates}
                showPresets={true}
              />
              <div className="events-list-autocomplete-container">
                <div
                  className="filter-buttons"
                  data-testid="filter-buttons"
                  onClick={() => setShowExtraFilters(!showExtraFilters)}
                >
                  <Filter className="filter-icon" />
                  {showExtraFilters ? (
                    <ChevronUp className="filter-icon" />
                  ) : (
                    <ChevronDown className="filter-icon" />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="events-list-content">
            <div className="events-list-content-header"
            style={{ display: children ? "flex" : "none" }}
            >
              {children}
            </div>
            <div
              className="events-list-selectors"
              style={{ display: showExtraFilters ? "flex" : "none" }}
            >
              <div className="events-list-selector">
                <span>{AppStrings.PROVIDER}</span>
                <Select
                  placeholder="Not Selected"
                  value={selectedProvider}
                  onChange={(value) => handleProviderChange(value)}
                  options={providers}
                />
              </div>
              <div className="events-list-selector">
                <span>{AppStrings.SPORT}</span>
                <CustomAutoCompleteControlled
                  placeholder="Not Selected"
                  value={selectedSport}
                  allOptions={sports}
                  handleChange={(value) => handleSportChange(value)}
                  clearOptions={() => {
                    setSelectedSport(null);
                    setSelectedSportId(null);
                    setSelectedLeague(null);
                    setSelectedTournamentId(null);
                  }}
                  selector="value"
                />
                {/* <Select
                  placeholder="Not Selected"
                  value={selectedSport}
                  onChange={(value) => handleSportChange(value)}
                  options={sports}
                /> */}
              </div>
              <div className="events-list-selector">
                <span>{AppStrings.LEAGUE}</span>
                <CustomAutoCompleteControlled
                  placeholder="Not Selected"
                  value={selectedLeague}
                  allOptions={leagues}
                  handleChange={(value) => handleLeagueChange(value)}
                  clearOptions={() => {
                    setSelectedLeague(null);
                    setSelectedTournamentId(null);
                  }}
                  selector="value"
                />
              </div>
              <div className="events-list-selector">
                <span>{AppStrings.EVENT}</span>
                <CustomAutoCompleteControlled
                  placeholder="Not Selected"
                  value={selectedEvent}
                  allOptions={events}
                  handleChange={(value) => {
                    // value is the event_id from selector="label", but we need to find the actual event
                    const eventOption = events.find((event) => event.label === value || event.value === value);
                    setSelectedEvent(eventOption ? eventOption.label : value);
                  }}
                  clearOptions={() => setSelectedEvent(null)}
                  selector="label"
                  onSearchChange={handleEventSearch}
                />
              </div>
              <div className="events-list-selector">
                <span>{AppStrings.EVENT_TYPE}</span>
                <Select
                  placeholder="Not Selected"
                  value={selectedEventType}
                  onChange={(value) => handleEventTypeChange(value)}
                  options={eventTypes}
                />
              </div>
              <div className="events-list-selector">
                <span>{AppStrings.STATUS}</span>
                <Select
                  placeholder="Not Selected"
                  value={selectedStatus}
                  onChange={(value) => handleStatusChange(value)}
                  options={statuses}
                />
              </div>
            </div>
            {showExtraFilters && (
              <div className="events-list-selector-action-buttons">
                <button className="clear" onClick={() => handleClearFilters()}>
                  {AppStrings.CLEAR_FILTERS}
                </button>
                <button className="apply" onClick={() => handleApplyFilters()}>
                  {AppStrings.APPLY}
                </button>
              </div>
            )}
            {isApiRunning ? (
              <div className="events-list-loader-container">
                <RebetLoader height={"100px"} width={"100px"} />
              </div>
            ) : (
              <div className="events-list-kpis-table">
                <Table
                  columns={columns}
                  dataSource={reorderedData}
                  pagination={false}
                  scroll={{ x: 2500 }}
                  rowKey={(record) => record.bet_id}
                  sticky
                  rowClassName={(record) =>
                    pinnedEvents.filter(
                      (pinnedEvent) => pinnedEvent.id === record.id
                    ).length > 0
                      ? "pinned-row"
                      : ""
                  }
                  // onRow={(record) => ({
                  //   style: { cursor: "pointer" },
                  //   onClick: () => {
                  //     navigate(`/event-details?bet-id=${record.bet_id}`);
                  //   },
                  // })}
                />
                <div className="sports-bets-pagination-container">
                  <Pagination
                    current={pagination.current_page}
                    total={pagination.total_results}
                    pageSize={20}
                    showSizeChanger={false}
                    onChange={(page) => {
                      if (
                        page === pagination.current_page - 1 ||
                        page === pagination.current_page + 1
                      ) {
                        handlePaginationChange(page);
                      }
                    }}
                    itemRender={(page, type, originalElement) => {
                      if (type === "page") {
                        return (
                          <button
                            type="button"
                            disabled={page > pagination.current_page + 1}
                            className={`${
                              page > pagination.current_page + 1
                                ? "disabled"
                                : ""
                            }`}
                          >
                            {page}
                          </button>
                        );
                      }
                      return originalElement;
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      </div>
    </div>
  );
}

export default EventsList;
